import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/Button';
import { TrustBadge } from '../components/TrustBadge';
import { ServiceCard } from '../components/ServiceCard';
import { LayoutDashboardIcon, UsersIcon, MessageSquareIcon, LockIcon } from 'lucide-react';
export function Home() {
  return <div className="min-h-screen bg-gradient-to-b from-white to-slate-50" data-id="element-267">
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-id="element-268">
        <div className="text-center max-w-4xl mx-auto" data-id="element-269">
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-270">
            Simple Software for Clearer, Faster Daily Work.
          </h1>
          <p className="text-xl text-slate-600 mb-8 leading-relaxed" data-id="element-271">
            Domisafe helps businesses manage tasks, communication and customer
            information in one organised place.
          </p>
          <Link to="/contact" data-id="element-272">
            <Button variant="primary" size="lg" data-id="element-273">
              Contact Us
            </Button>
          </Link>
        </div>

        {/* Hero Image */}
        <div className="mt-16 rounded-2xl overflow-hidden shadow-2xl" data-id="element-274">
          <img src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=1200&h=600&fit=crop" alt="Team collaboration" className="w-full h-96 object-cover" data-id="element-275" />
        </div>
      </section>

      {/* Intro Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-276">
        <div className="bg-white rounded-2xl shadow-lg p-12" data-id="element-277">
          <div className="max-w-3xl mx-auto text-center" data-id="element-278">
            <h2 className="text-3xl font-bold text-slate-900 mb-6" data-id="element-279">
              Everything You Need in One Place
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-8" data-id="element-280">
              Domisafe is a subscription-based tool that improves organisation,
              speeds up communication and keeps key information in one location.
              No downloads or complicated setup required—simply log in and start
              working more efficiently.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed" data-id="element-281">
              Whether you're managing customer records, tracking daily tasks or
              coordinating with your team, Domisafe brings clarity to your
              workflow and helps you focus on what matters most.
            </p>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-282">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-283">
          <div className="bg-white rounded-xl p-6 shadow-md" data-id="element-284">
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4" data-id="element-285">
              <LayoutDashboardIcon className="w-6 h-6 text-emerald-600" data-id="element-286" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2" data-id="element-287">
              Task Tracking
            </h3>
            <p className="text-slate-600" data-id="element-288">
              Organise and monitor your daily tasks in one clear dashboard.
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md" data-id="element-289">
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4" data-id="element-290">
              <UsersIcon className="w-6 h-6 text-emerald-600" data-id="element-291" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2" data-id="element-292">
              Customer Records
            </h3>
            <p className="text-slate-600" data-id="element-293">
              Store and access customer information securely whenever you need
              it.
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md" data-id="element-294">
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4" data-id="element-295">
              <MessageSquareIcon className="w-6 h-6 text-emerald-600" data-id="element-296" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2" data-id="element-297">
              Message Dashboard
            </h3>
            <p className="text-slate-600" data-id="element-298">
              Keep all your communication organised and easy to find.
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md" data-id="element-299">
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4" data-id="element-300">
              <LockIcon className="w-6 h-6 text-emerald-600" data-id="element-301" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2" data-id="element-302">
              Secure Access
            </h3>
            <p className="text-slate-600" data-id="element-303">
              Your data is protected with industry-standard security measures.
            </p>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-304">
        <div className="flex flex-wrap gap-6 justify-center" data-id="element-305">
          <TrustBadge icon="location" text="UK-Based" data-id="element-306" />
          <TrustBadge icon="payment" text="Secure Payments" data-id="element-307" />
          <TrustBadge icon="star" text="Trusted Service" data-id="element-308" />
          <TrustBadge icon="shield" text="Clear Pricing" data-id="element-309" />
        </div>
      </section>

      {/* Pricing Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-310">
        <div className="text-center mb-12" data-id="element-311">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-312">
            Choose Your Package
          </h2>
          <p className="text-xl text-slate-600" data-id="element-313">
            Simple, transparent pricing for businesses of all sizes
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-314">
          <ServiceCard name="Bronze" price="£99" features={['Task tracking', 'Customer record storage', 'Message dashboard', 'Secure access']} paymentLink="https://buy.stripe.com/00w6oHbtE7d6epQc2jeAg01" image="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop" data-id="element-315" />

          <ServiceCard name="Silver" price="£120" features={['Everything in Bronze', 'Team access', 'Activity monitoring', 'Automated reminders']} paymentLink="https://buy.stripe.com/4gM14n41c9le6Xo7M3eAg00" image="https://images.unsplash.com/photo-1551434678-e076c223a692?w=400&h=300&fit=crop" popular={true} data-id="element-316" />
        </div>

        <div className="text-center mt-12" data-id="element-317">
          <Link to="/service-details" data-id="element-318">
            <Button variant="outline" size="lg" data-id="element-319">
              View Full Service Details
            </Button>
          </Link>
        </div>
      </section>
    </div>;
}